import axiosInstance from "@/api/axios-instance";
import { Bundle } from "@/pages/bundles/types/bundle";

// The server caps `limit` at 100 per page (FindAllBundleDto @Max(100)), and the
// active catalog is past that — a single page silently dropped bundles from
// every picker (RH-990: "Online psychiatry evaluation" was unselectable in the
// coupon editor, and "Select All" only added the same first 100).
//
// Paged here on purpose rather than adding an unpaginated server mode: the
// catalog is ~115 bundles (2 pages), and this works against old and new
// servers alike, so it carries no deploy-order coupling. The server's
// findAllActive reads the whole catalog and slices in memory, so each page is
// a full read — revisit with a server-side "all" mode if the catalog grows
// well past a few hundred.
const PAGE_SIZE = 100;
// Runaway guard against a malformed lastPage: 50 pages = 5000 bundles.
const MAX_PAGES = 50;

interface ActiveBundlesPage {
  bundles: Bundle[];
  total: number;
  page: number;
  lastPage: number;
  nextPage: number | null;
}

export interface ActiveBundles {
  bundles: Bundle[];
  total: number;
}

async function getActiveBundlesPage(page: number, regionId?: string) {
  // region_id scopes the list server-side via the Bundle→Region relation —
  // the canonical filter. (The legacy Bundle.region string drifted from
  // Region.code — 'uae' vs 'ae' — so never compare those client-side.)
  const params = new URLSearchParams({
    limit: String(PAGE_SIZE),
    page: String(page),
  });
  if (regionId) params.set("region_id", regionId);
  const response = await axiosInstance.get(`/bundle/active?${params}`);

  if (!response.status.toString().startsWith("2")) {
    throw new Error("Failed to fetch active bundles");
  }

  return response.data as ActiveBundlesPage;
}

// One pass over every page: page 0 reveals lastPage, the rest run in parallel.
async function fetchAllPages(regionId?: string) {
  const first = await getActiveBundlesPage(0, regionId);
  const reportedPages = (first.lastPage ?? 0) + 1;
  const truncated = reportedPages > MAX_PAGES;
  if (truncated) {
    console.warn(
      `getActiveBundles: server reported ${reportedPages} pages, capping at ${MAX_PAGES} — list is truncated`
    );
  }

  const rest = await Promise.all(
    Array.from({ length: Math.min(reportedPages, MAX_PAGES) - 1 }, (_, i) =>
      getActiveBundlesPage(i + 1, regionId)
    )
  );

  // Pages are separate server reads, so a bundle created between them shifts
  // the offsets and can repeat across a page boundary — dedupe by id.
  const seen = new Set<string>();
  const bundles = [first, ...rest]
    .flatMap((p) => p.bundles ?? [])
    .filter((bundle) => {
      if (seen.has(bundle.id)) return false;
      seen.add(bundle.id);
      return true;
    });

  // The same shift can also SKIP a row, which dedupe can't recover: the server
  // orders by created_at alone and prod has many tied rows (bundles created in
  // one transaction), so tied rows at a page boundary may land differently in
  // two reads — as can a bundle retired mid-fetch. `total` is the server's
  // deduped count for the page-0 read, so coming up short means a skip.
  const short = !truncated && bundles.length < first.total;
  return { bundles, short };
}

// Returns EVERY active bundle (all pages). Consumers read `.bundles`; `total`
// is the length of that merged list, not a server count.
export async function getActiveBundles(
  regionId?: string
): Promise<ActiveBundles> {
  let result = await fetchAllPages(regionId);
  // A skip is a race between two reads, so one fresh pass settles it.
  if (result.short) result = await fetchAllPages(regionId);
  if (result.short) {
    console.warn(
      "getActiveBundles: merged list is still shorter than the server's count after a retry — some bundles may be missing"
    );
  }
  return { bundles: result.bundles, total: result.bundles.length };
}
